/**
 * TenantOverrideEditSlideout — Stage 3c-1 (P0-IDENT).
 *
 * Edit form for per-tenant identity-plane overrides. Fields cover the
 * V200 identity_tenant_overrides row (sparse — null fields preserve
 * existing values):
 *
 *   - trustDomain (DNS-form override)
 *   - complianceAdditions (array of regoTag strings — ADD ONLY per
 *     decision §1; cannot subtract from Org's set)
 *   - federationOverrides (array of peer UUIDs — multi-select once
 *     the peer picker lands; for now, comma-separated UUIDs)
 *
 * No rotation TTL — per design §3.3, rotation policy lives at instance
 * scope, not tenant. No dedicated SPIRE field yet (the JSONB column is
 * complex enough to warrant its own future editor).
 *
 * Reached from IdentityTenantDetailPageV2 via the per-dimension card
 * footer buttons. Two modes:
 *   - mode='add'    — form for non-null overrides
 *   - mode='remove' — confirm dialog → DELETE
 */
import React, { useEffect, useState } from 'react';
import { Save, X as XIcon, AlertTriangle, Trash2 } from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Pill } from '../Pill';
import {
  updateIdentityTenantOverride,
  resetIdentityTenantOverride,
} from '../../../services/api';

export function TenantOverrideEditSlideout({
  isOpen,
  onClose,
  onSaved,
  tenantId,
  tenantName,
  mode = 'add',
}) {
  const [trustDomain, setTrustDomain] = useState('');
  const [complianceAdditions, setComplianceAdditions] = useState('');
  const [federationOverrides, setFederationOverrides] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) return;
    setTrustDomain(''); setComplianceAdditions(''); setFederationOverrides('');
    setError(null);
  }, [isOpen]);

  const inputCls =
    'v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]';

  const handleAdd = async () => {
    setError(null);
    const body = {};
    if (trustDomain.trim()) body.trustDomain = trustDomain.trim().toLowerCase();
    if (complianceAdditions.trim()) {
      body.complianceAdditions = complianceAdditions.split(',').map(s => s.trim()).filter(Boolean);
    }
    if (federationOverrides.trim()) {
      const uuids = federationOverrides.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
      // Cheap UUID-form check — server validates strictly.
      const malformed = uuids.find(u => !/^[0-9a-fA-F-]{36}$/.test(u));
      if (malformed) {
        setError(`Not a valid UUID: ${malformed}`);
        return;
      }
      body.federationOverrides = uuids;
    }
    if (Object.keys(body).length === 0) {
      setError('Set at least one field to install an override.');
      return;
    }

    setSaving(true);
    const res = await updateIdentityTenantOverride(tenantId, body);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Save failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(res?.data);
    onClose();
  };

  const handleRemove = async () => {
    setError(null);
    setSaving(true);
    const res = await resetIdentityTenantOverride(tenantId);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Delete failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(null);
    onClose();
  };

  const isRemove = mode === 'remove';

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={520}
      ariaLabel={isRemove ? 'Remove tenant override' : 'Install tenant override'}
      header={
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            {isRemove ? 'Remove tenant override' : 'Install tenant override'}
          </h2>
          <Pill tone={isRemove ? 'danger' : 'trust'} size="sm">
            {isRemove ? 'Destructive' : 'Override'}
          </Pill>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" iconLeft={<XIcon size={13} />} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {isRemove ? (
            <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />}
                    onClick={handleRemove} disabled={saving}>
              {saving ? 'Removing…' : 'Remove override'}
            </Button>
          ) : (
            <Button variant="primary" size="sm" iconLeft={<Save size={13} />}
                    onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : 'Install override'}
            </Button>
          )}
        </div>
      }
    >
      <div className="px-4 py-4 flex flex-col gap-4">
        {error && (
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md px-3 py-2 text-[12.5px] text-v2-danger flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="text-[12.5px] text-v2-ink-secondary">
          Tenant: <span className="text-v2-ink font-semibold">{tenantName || tenantId}</span>
        </div>

        {isRemove ? (
          <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-md px-3 py-2.5 text-[12.5px] text-v2-warning">
            Removing the override reverts every dimension to inherit from Org.
            This deletes the identity_tenant_overrides row; the tenant continues
            to operate but loses any per-tenant identity customisations.
          </div>
        ) : (
          <>
            <div className="text-[11px] text-v2-ink-muted">
              Sparse update — leave a field blank to preserve the current value.
              At least one field must be set.
            </div>
            <div>
              <label className="block text-[11px] text-v2-ink-secondary mb-1">
                Trust domain override (DNS form)
              </label>
              <input type="text" value={trustDomain}
                     onChange={(e) => setTrustDomain(e.target.value)}
                     placeholder="fraud-aml.acmebank.spiffe.io" className={inputCls} />
              <div className="text-[10.5px] text-v2-ink-muted mt-1">
                Rare — only for tenants operating a logically distinct SPIFFE root.
              </div>
            </div>
            <div>
              <label className="block text-[11px] text-v2-ink-secondary mb-1">
                Compliance additions (regoTag strings, comma-separated)
              </label>
              <input type="text" value={complianceAdditions}
                     onChange={(e) => setComplianceAdditions(e.target.value)}
                     placeholder="MIFID-AUTONOMOUS-FIN, FINCEN-CRITICAL" className={inputCls} />
              <div className="text-[10.5px] text-v2-ink-muted mt-1">
                ADD-only — these append to the Org's compliance binding set.
                Tenants cannot remove Org-mandated frameworks.
              </div>
            </div>
            <div>
              <label className="block text-[11px] text-v2-ink-secondary mb-1">
                Federation override (peer UUIDs, comma-separated)
              </label>
              <input type="text" value={federationOverrides}
                     onChange={(e) => setFederationOverrides(e.target.value)}
                     placeholder="uuid-1, uuid-2" className={inputCls} />
              <div className="text-[10.5px] text-v2-ink-muted mt-1">
                Replaces the Org's federation peer list for this tenant.
                Peer UUIDs come from /api/v1/identity/federation-peers.
              </div>
            </div>
          </>
        )}
      </div>
    </Slideout>
  );
}

export default TenantOverrideEditSlideout;
